// Single-form gate that takes BOTH the org description and the email.
// Submitting POSTs /api/unlock-email (sets the unlock cookie) then hands the
// org chart up to the parent so DiagnosisStream can begin streaming.
//
// Turnstile widget is lazy-loaded; site key from VITE_TURNSTILE_SITE_KEY.
// In dev, use Cloudflare's always-pass test key 1x00000000000000000000AA
// with secret 1x0000000000000000000000000000000AA in .dev.vars.

import { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle, Check, MessageSquare } from 'lucide-react';
import { unlockEmail } from '../lib/api';
import { loadTurnstile, TURNSTILE_SITE_KEY } from '../lib/turnstile';
import { identify, track } from '../lib/mixpanel';
import type { FitState } from './WhereDoIFit';

export interface EmailGateUnlock {
  email: string;
  orgChart: string;
}

export default function EmailGate({
  onUnlocked,
  fitState,
}: {
  onUnlocked: (data: EmailGateUnlock) => void;
  fitState?: FitState | null;
}) {
  const [orgChart, setOrgChart] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Lazy-load Turnstile and render the widget once the script is available.
  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled) return;
        if (!widgetContainerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(widgetContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setError('Verification check failed. Please try again.'),
          'expired-callback': () => setTurnstileToken(null),
          size: 'flexible',
        });
        setTurnstileReady(true);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore - widget may already be gone if React strict-mode double-mounted.
        }
      }
    };
  }, []);

  const canSubmit =
    !loading &&
    orgChart.trim().length >= 20 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    turnstileToken !== null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !turnstileToken) return;
    setLoading(true);
    setError(null);
    const cleanEmail = email.trim().toLowerCase();
    track('email_gate_submitted', { // event 8
      has_fit_result: !!fitState,
      job_title: fitState?.jobTitle ?? null,
      department: fitState?.result.department ?? null,
    });
    try {
      await unlockEmail(cleanEmail, turnstileToken);
      // Tie the prior anonymous funnel to the lead - this is what Mixpanel
      // earns over Plausible: per-lead funnel visibility before they speak to you.
      identify(cleanEmail);
      onUnlocked({ email: cleanEmail, orgChart: orgChart.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not unlock. Please try again.');
      // Reset Turnstile so the user can re-verify.
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
      setTurnstileToken(null);
      setLoading(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-paper-deep border-b border-border py-24">
      <img
        src="/logo-green.png"
        alt=""
        className="absolute top-1/2 -right-44 -translate-y-1/2 w-[520px] aspect-square opacity-[0.05] pointer-events-none"
      />
      <div className="relative max-w-[1180px] mx-auto px-6 grid grid-cols-1 md:grid-cols-[5fr_7fr] gap-16 items-start">
        <div>
          <div className="text-[12px] font-semibold tracking-mega uppercase text-green-700 mb-3">
            The Pressure Test
          </div>
          <h2 className="font-display text-[clamp(28px,3.5vw,36px)] font-semibold tracking-[-0.02em] leading-[1.1] text-ink mb-4">
            What we'd say to your board, if asked.
          </h2>
          <p className="text-[15px] text-ink-mute leading-[1.7] max-w-[480px]">
            Describe your organisation in three or four sentences. We'll read it through the Intelligence Tier lens and write you the diagnosis. The brief draws on three other lenses identified for your organisation specifically.
          </p>
          <ul className="mt-8 flex flex-col gap-3 list-none p-0">
            {[
              'Diagnosis written in under sixty seconds.',
              'No mailing list. We use the email only if you book a call.',
              'Your input is not used to train any model.',
            ].map((line) => (
              <li key={line} className="flex gap-3 items-start text-[13px] text-ink-mute leading-[1.55]">
                <Check size={16} className="flex-shrink-0 text-green-700 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-border rounded-xl p-8 shadow-elev-1">
          <div className="mb-6">
            <label className="flex justify-between items-baseline text-[11px] font-semibold tracking-mega uppercase text-stone-500 mb-2">
              <span>Your organisation</span>
              <span className="text-ink-faint font-medium tracking-[0.02em] normal-case">
                {orgChart.length} / 2000
              </span>
            </label>
            <textarea
              value={orgChart}
              onChange={(e) => setOrgChart(e.target.value)}
              placeholder="e.g. Mid-sized B2B SaaS, ~280 people. Engineering and Product report to the CEO; Marketing, Sales and Customer Success sit under a CRO. We've stalled on growth for two quarters and are losing senior people."
              maxLength={2000}
              rows={7}
              required
              disabled={loading}
              className="w-full bg-paper-light border border-border-strong rounded-md px-4 py-3 text-base sm:text-[14px] text-ink placeholder:text-stone-400 focus:outline-none focus:bg-white focus:border-green-700 focus:ring-2 focus:ring-[rgba(139,184,216,0.45)] transition-colors disabled:opacity-60 leading-[1.6] resize-none"
            />
            {orgChart.trim().length < 20 && (
              <div className="mt-1.5 text-[11px] text-ink-faint tracking-[0.02em]">
                A few sentences gives us enough to work with.
              </div>
            )}
          </div>

          <div className="mb-6">
            <label className="flex justify-between items-baseline text-[11px] font-semibold tracking-mega uppercase text-stone-500 mb-2">
              <span>Your email</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              maxLength={254}
              required
              disabled={loading}
              className="w-full bg-paper-light border border-border-strong rounded-md px-4 py-3 text-base sm:text-[14px] text-ink placeholder:text-stone-400 focus:outline-none focus:bg-white focus:border-green-700 focus:ring-2 focus:ring-[rgba(139,184,216,0.45)] transition-colors disabled:opacity-60"
            />
            <div className="mt-1.5 text-[11px] text-ink-faint tracking-[0.02em]">
              We'll send a confirmation and use this if you book the call. Nothing else.
            </div>
          </div>

          <div ref={widgetContainerRef} className="turnstile-container min-h-[65px] mb-6 w-full" />

          {error && (
            <div className="flex items-start gap-2 text-[13px] text-clay mb-4">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-green-700 text-paper px-7 py-3.5 rounded-md text-[15px] font-medium flex items-center justify-center gap-2.5 hover:bg-green-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <MessageSquare size={18} />
            )}
            {loading ? 'Reading…' : 'Read my organisation'}
          </button>

          {!turnstileReady && (
            <div className="text-[11px] text-stone-500 mt-3">Loading verification…</div>
          )}
        </form>
      </div>
    </section>
  );
}
